import React from "react";
import ImagePostCarouselTag from "../GeneralPostTag/ImagePostCarouselTag";
import ImagePostTag from "../GeneralPostTag/ImagePostTag";
import VideoPostTag from "../GeneralPostTag/VideoPostTag";
import ImagePostTagLocal from "../GeneralPostTag/ImagePostTagLocal";
import IndividualPostCarouselTag from "../GeneralPostTag/IndividualPostCarouselTag";
import IndividualImagePostTag from "../GeneralPostTag/IndividualImagePostTag";
import IndividualVideoPostTag from "../GeneralPostTag/IndividualVideoPostTag";
import { normalizeSoundValue } from "../../Utils/helpers/carouselHelper";
import {
  isYouTubePlatformPost,
  resolveFeedCardKind,
  resolvePlayableVideoUrl,
} from "../../Utils/media/feedMedia";
import {
  contentCarouselClickName,
  isContentPlatform,
} from "../../Utils/media/feedCardTitle";
import track, { TRACKING_TYPES } from "../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../Utils/SupabaseServices/TrackTemplate";
import { legacyLibAnalyticsPathname } from "../../Utils/analytics/legacyLibAnalyticsPath";
import { supabasePublic } from "../../config/supabaseClient";

/**
 * Single media router for dashboard, public LiB, website feeds, and individual pages.
 * All video vs still vs product-still decisions live in `resolveFeedCardKind`.
 */
const CarouselPostMedia = ({
  link,
  handleShop,
  title,
  brand_color,
  isLinks,
  productName,
  contentShape,
  contentSound,
  isSoundOn: isSoundOnProp,
  type,
  /** Carousel/component type (this week / content / …) — not the post type. */
  componentType,
  pinnedPostIds,
  isDashboard = true,
  designTokens,
  from,
  enableInteractiveMedia,
  disableHoverPlayback,
  variant = "feed",
  mediaIndex,
  isPodcast,
  treatProductTypeAsStill,
  forceProductFrame,
}: any) => {
  const resolvedSoundOn = normalizeSoundValue(
    isSoundOnProp !== undefined ? isSoundOnProp : contentSound,
    true,
  );
  const isHasMediaUrl =
    link?.type === "FEED" && link?.platform !== "MISC-content";
  const isYoutube = isYouTubePlatformPost(link) && !link?.product_image;
  // Prefer explicit carousel type so day-windows keep post media (not product stills).
  const resolvedComponentType = String(componentType || type || "");
  const kind = resolveFeedCardKind(link, {
    isLinks,
    componentType: resolvedComponentType,
    treatProductTypeAsStill,
    isPodcast,
  });
  const useProductStill = kind === "shoppable-still";
  // Prefer the post's own type (REELS/VIDEO/…) over the carousel component type
  // (`content` / `query-content` / empty). Component types are not definite
  // video types and would make resolvePlayableVideoUrl return "" for
  // extensionless storage URLs — blank “Media unavailable” tiles.
  const playableVideo = resolvePlayableVideoUrl({
    mediaUrl: link?.media_url,
    storageUrl: link?.storage_url,
    type: String(link?.type || type || ""),
    platform: link?.platform,
  });
  const shared = {
    title,
    brand_color,
    isLinks,
    isDashboard,
    isHasMediaUrl,
    isYoutube,
    contentShape,
    // productName,
    // Pass post type into video tags so playable URL + poster logic stay aligned
    // with resolveFeedCardKind (component type stays on `kind` via componentType).
    type: link?.type || type,
    pinnedPostIds,
    designTokens,
    from,
  };
  const trackedHandleShop = (...args: any[]) => {
    if (typeof handleShop !== "function") return;
    const next = [...args];
    if (from === "landing-page" && isContentPlatform(link?.platform)) {
      const visible = contentCarouselClickName(link, title);
      if (visible) next[3] = visible;
    }
    trackLinkInBioEmbedClick(link, next, title);
    return handleShop(...next);
  };

  if (kind === "podcast") {
    return (
      <ImagePostTagLocal
        key={link.id}
        data={link}
        isPodcast
        brand_color={brand_color}
        content_shape={contentShape}
        isYoutube={false}
        pinnedPostIds={pinnedPostIds}
        type={type}
        isDashboard={isDashboard}
        designTokens={designTokens}
        from={from}
      />
    );
  }

  if (variant === "individual") {
    if (kind === "video") {
      // YouTube → same canva tile as LiB frontend / search (VideoPostTag
      // short-circuits the same way). Hover player is for IG/TikTok/etc.
      if (isYoutube) {
        return (
          <IndividualImagePostTag
            data={link}
            handleRedirect={handleShop}
            productName={productName}
            isSoundOn={resolvedSoundOn}
            mediaIndex={mediaIndex}
            forceProductFrame={Boolean(forceProductFrame) || useProductStill}
            suppressProductStill={!useProductStill}
            {...shared}
            isDashboard={useProductStill ? true : isDashboard}
            isLinks={isLinks}
          />
        );
      }
      return (
        <IndividualVideoPostTag
          post={link}
          handleRedirect={trackedHandleShop}
          thumbnail={link.thumbnail}
          media_url={playableVideo || link?.media_url}
          productName={productName}
          isSoundOn={resolvedSoundOn}
          mediaIndex={mediaIndex}
          {...shared}
        />
      );
    }
    if (kind === "multi-still") {
      return (
        <IndividualPostCarouselTag
          post={link}
          handleRedirect={trackedHandleShop}
          productName={productName}
          mediaIndex={mediaIndex}
          {...shared}
        />
      );
    }
    return (
      <IndividualImagePostTag
        data={link}
        handleRedirect={trackedHandleShop}
        productName={productName}
        isSoundOn={resolvedSoundOn}
        mediaIndex={mediaIndex}
        forceProductFrame={Boolean(forceProductFrame) || useProductStill}
        suppressProductStill={!useProductStill}
        {...shared}
        isDashboard={useProductStill ? true : isDashboard}
        isLinks={isLinks}
      />
    );
  }

  if (kind === "video") {
    return (
      <VideoPostTag
        post={link}
        handleRedirect={trackedHandleShop}
        thumbnail={link.thumbnail}
        media_url={playableVideo || link?.media_url}
        isSoundOn={resolvedSoundOn}
        enableInteractiveMedia={enableInteractiveMedia}
        disableHoverPlayback={disableHoverPlayback}
        suppressProductStill={!useProductStill}
        {...shared}
      />
    );
  }

  if (kind === "multi-still") {
    return (
      <ImagePostCarouselTag
        post={link}
        handleRedirect={trackedHandleShop}
        isSoundOn={resolvedSoundOn}
        enableInteractiveMedia={enableInteractiveMedia}
        disableHoverPlayback={disableHoverPlayback}
        {...shared}
      />
    );
  }

  return (
    <ImagePostTag
      data={link}
      handleRedirect={trackedHandleShop}
      forceProductFrame={Boolean(forceProductFrame) || useProductStill}
      suppressProductStill={!useProductStill}
      {...shared}
    />
  );
};

function trackLinkInBioEmbedClick(link: any, args: any[], carouselTitle: unknown) {
  if (typeof window === "undefined") return;
  if (!/\/component\//.test(window.location.pathname)) return;
  const params = new URLSearchParams(window.location.search);
  const blockId = params.get("sb_lib");
  const slug = window.location.pathname.split("/").filter(Boolean)[0];
  const userId = params.get("sb_uid") || link?.user_id;
  const postId = args[0];
  const name = isContentPlatform(link?.platform)
    ? contentCarouselClickName(link, carouselTitle) || args[3]
    : args[3] || link?.product_name || link?.shop_title;
  if (!blockId || !slug || !userId || !postId) {
    if (window.parent === window) return;
    const payload = {
      type: "SB_EMBED_CLICK",
      post_id: postId,
      media_url: args[1],
      platform: args[2],
      name,
      title: args[4],
      isLinks: args[5],
    };
    window.parent.postMessage(payload, "*");
    if (window.parent.parent && window.parent.parent !== window.parent) {
      window.parent.parent.postMessage(payload, "*");
    }
    return;
  }
  const libPath = legacyLibAnalyticsPathname(slug);
  const details = {
    post_id: postId,
    media_url: args[1],
    platform: args[2],
    name,
    title: name,
    component_id: blockId,
  };
  void track("Post Click", TRACKING_TYPES.EVENT, {
    pathname: libPath,
    user_id: userId,
    title: "Stay Up To Date",
    details,
  });
  void trackTemplate(
    "Post Click",
    TEMPLATE_TRACKING_TYPES.EVENT,
    {
      user_id: String(userId),
      username: slug,
      route_name: libPath,
      pathname: libPath,
      page_key: "linkInBio",
      title: "Website Template",
      section_id: "linkInBioStack",
      section_type: "LinkInBioStack",
      entity_type: "post",
      entity_id: String(postId),
      entity_label: String(name || postId),
      carousel_slug: blockId,
      details,
    },
    supabasePublic,
  );
}

export default CarouselPostMedia;
